import * as React from "react";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { Check, ChevronDown, Globe, LogOut, Moon, PanelLeftClose, PanelLeftOpen, RefreshCw, Search, Sun } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarRail, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { currentRole, navigation } from "@/lib/dashboard-data";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { LanguageProvider, useLanguage } from "@/components/dashboard/language-provider";
import { languages } from "@/lib/i18n";
import { logout } from "@/lib/auth";

function DeuxdaMark() {
  return <img src="/app-icon.png" alt="Deuxda" className="size-9 shrink-0 rounded-md" />;
}

function AppSidebar() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [query, setQuery] = React.useState("");
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const visible = navigation.filter((item) => (!item.roles || item.roles.includes(currentRole)) && (`${item.title} ${item.subtitle}`).toLowerCase().includes(query.toLowerCase()));
  return (
    <Sidebar collapsible="icon" className="border-sidebar-border/70">
      <SidebarHeader className="gap-4 border-b border-sidebar-border/60 p-4 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-2">
        <Link to="/" className="flex min-w-0 items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring">
          <DeuxdaMark />
          {!collapsed && <div className="min-w-0"><div className="truncate font-heading text-lg font-bold text-sidebar-foreground">Deuxda <span className="font-semibold text-muted-foreground">Admin</span></div><div className="text-[10px] font-bold uppercase text-primary">Piloting</div></div>}
        </Link>
        {!collapsed && <div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"/><SidebarInput value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="Search navigation..." className="h-9 rounded-lg border-sidebar-border/80 bg-background/45 pl-9 backdrop-blur-sm" aria-label="Search navigation"/></div>}
      </SidebarHeader>
      <SidebarContent className="py-2">
        <nav aria-label="Main navigation">
          <SidebarMenu className="px-2 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-1.5">
            {visible.map((item) => {
              const active = pathname === item.href;
              const Icon = item.icon;
              return <SidebarMenuItem key={item.key}>
                <SidebarMenuButton asChild isActive={active} tooltip={item.title} size="lg" className="h-11 rounded-lg border border-transparent px-3 transition-[background-color,border-color,box-shadow] data-[active=true]:border-sidebar-border/70 data-[active=true]:bg-card/75 data-[active=true]:text-primary data-[active=true]:shadow-sm group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!justify-center group-data-[collapsible=icon]:[&>span]:hidden">
                  <Link to={item.href} onClick={()=>setOpenMobile(false)}>
                    <Icon className="size-4" />
                    <span className="min-w-0"><span className="block truncate text-[13px] font-medium leading-4">{item.title}</span><span className="block truncate text-[10px] font-normal text-muted-foreground">{item.subtitle}</span></span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>;
            })}
          </SidebarMenu>
        </nav>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border/60 p-3 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Logout"
              className="rounded-lg group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!justify-center group-data-[collapsible=icon]:[&>span]:hidden"
              onClick={() => {
                logout();
                toast.success("Signed out successfully");
                window.location.href = "/login";
              }}
            >
              <LogOut />
              <span>Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

function LanguagePicker() {
  const { language, setLanguage } = useLanguage();
  const active = languages.find((item)=>item.value === language) ?? languages[0]!;
  return <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" size="sm" className="gap-1.5 bg-card/60 shadow-sm backdrop-blur-sm" aria-label="Change language" title="Change language">
        <Globe className="size-4"/><span className="hidden text-xs font-semibold uppercase sm:inline">{active.value}</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-44">
      <DropdownMenuLabel>Language</DropdownMenuLabel>
      <DropdownMenuSeparator/>
      {languages.map((item)=>(
        <DropdownMenuItem key={item.value} onClick={()=>{ setLanguage(item.value); toast.success("Language updated"); }}>
          <span>{item.flag}</span><span className="flex-1" data-no-translate>{item.native}</span>{language === item.value && <Check className="size-4 text-primary"/>}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>;
}

function TopBar() {
  const router = useRouter();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const current = navigation.find((item)=>item.href === pathname);
  const currentTitle = current?.title ?? "Deuxda Admin";
  const currentSubtitle = current?.subtitle ?? "Marketplace administration";
  const [refreshing, setRefreshing] = React.useState(false);
  const [theme, setTheme] = usePersistedState<"light"|"dark">("deuxda-theme", "light");
  React.useEffect(()=>{ document.documentElement.classList.toggle("dark", theme === "dark"); }, [theme]);
  async function refresh() {
    setRefreshing(true);
    await new Promise((resolve)=>setTimeout(resolve, 650));
    await router.invalidate();
    setRefreshing(false);
    toast.success(`${currentTitle} data refreshed`);
  }
  const { state, isMobile } = useSidebar();
  const sidebarExpanded = state === "expanded";
  return <header className="sticky top-0 z-30 border-b border-border/70 bg-card/70 shadow-glass backdrop-blur-xl">
    <div className="grid min-h-16 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 lg:px-6">
      <SidebarTrigger className="size-9 rounded-full border border-border/80 bg-card/75 shadow-sm hover:bg-card" aria-label={isMobile ? "Open navigation" : sidebarExpanded ? "Collapse navigation" : "Expand navigation"} title={isMobile ? "Open navigation" : sidebarExpanded ? "Collapse navigation" : "Expand navigation"}>
        {sidebarExpanded && !isMobile ? <PanelLeftClose /> : <PanelLeftOpen />}
      </SidebarTrigger>
      <div className="min-w-0"><h1 className="truncate text-base font-semibold text-foreground">{currentTitle}</h1><p className="hidden truncate text-xs text-muted-foreground sm:block">{currentSubtitle}</p></div>
      <div className="flex shrink-0 items-center gap-2">
        <div className="relative hidden w-64 lg:block"><Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"/><Input className="border-border/80 bg-background/45 pl-9 shadow-none backdrop-blur-sm focus-visible:bg-card" placeholder="Search within this view..." aria-label="Search within this view"/></div>
        <Button variant="outline" size="sm" className="bg-card/60 shadow-sm backdrop-blur-sm" onClick={refresh} disabled={refreshing}><RefreshCw className={refreshing ? "animate-spin" : ""}/><span className="hidden sm:inline">Refresh</span></Button>
        <LanguagePicker/>
        <Button variant="ghost" size="icon" aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`} onClick={()=>setTheme(theme === "dark" ? "light" : "dark")}>{theme === "dark" ? <Sun/> : <Moon/>}</Button>
        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" className="h-10 px-1.5"><Avatar className="size-8"><AvatarFallback>EA</AvatarFallback></Avatar><span className="hidden text-left lg:block"><span className="block text-xs font-semibold">Eddy Admin</span><span className="block text-[10px] text-muted-foreground">{currentRole}</span></span><ChevronDown className="hidden lg:block"/></Button></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-52"><DropdownMenuLabel>Administrator</DropdownMenuLabel><DropdownMenuSeparator/><DropdownMenuItem><Check/> {currentRole}</DropdownMenuItem><DropdownMenuItem onClick={()=>toast.success("Profile settings opened")}>Profile settings</DropdownMenuItem></DropdownMenuContent></DropdownMenu>
      </div>
    </div>
  </header>;
}

export function AdminShell({ children }: { children: React.ReactNode }) {
  return <LanguageProvider><SidebarProvider><AppSidebar/><SidebarInset className="min-w-0 overflow-x-hidden"><TopBar/><div className="mx-auto w-full max-w-[1600px] p-4 lg:p-6">{children}</div></SidebarInset></SidebarProvider></LanguageProvider>;
}
